import React from 'react';
import { TimePicker, TimePickerProps, TimeRangePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { CustomComponentProps } from '../types';

export interface MyTimePickerProps extends CustomComponentProps, Omit<TimePickerProps, 'value' | 'onChange'> {
  format?: string;
  use12Hours?: boolean;
  showNow?: boolean;
  allowClear?: boolean;
}

const MyTimePicker: React.FC<MyTimePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select time',
  disabled,
  readonly,
  format = 'HH:mm:ss',
  use12Hours = false,
  showNow = true,
  allowClear = true,
  ...rest
}) => {
  const handleChange = (time: Dayjs | null) => {
    if (!readonly && onChange) {
      onChange(time);
    }
  };

  // Convert value to dayjs if it's a string
  const dayjsValue = value ? (typeof value === 'string' ? dayjs(value, format) : value) : null;

  return (
    <TimePicker
      value={dayjsValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled || readonly}
      format={format}
      use12Hours={use12Hours}
      showNow={showNow}
      allowClear={allowClear}
      {...(rest as TimePickerProps)}
    />
  );
};

export interface MyTimeRangePickerProps extends CustomComponentProps {
  format?: string;
  use12Hours?: boolean;
  allowClear?: boolean;
}

export const MyTimeRangePicker: React.FC<MyTimeRangePickerProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  readonly,
  format = 'HH:mm:ss',
  use12Hours = false,
  allowClear = true,
  ...rest
}) => {
  const handleChange = (times: [Dayjs | null, Dayjs | null] | null) => {
    if (!readonly && onChange) {
      onChange(times);
    }
  };

  const rangePlaceholder: [string, string] | undefined =
    Array.isArray(placeholder) && placeholder.length === 2
      ? [String(placeholder[0] || 'Start time'), String(placeholder[1] || 'End time')]
      : undefined;

  return (
    <TimePicker.RangePicker
      value={value}
      onChange={handleChange}
      placeholder={rangePlaceholder}
      disabled={disabled || readonly}
      format={format}
      use12Hours={use12Hours}
      allowClear={allowClear}
      {...(rest as TimeRangePickerProps)}
    />
  );
};

export default MyTimePicker;
